import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Sparkles, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useLab, nameA, nameB, isSolo } from "@/context/LabContext";
import { PositionsVsNeedsDialog } from "@/components/PositionsVsNeedsDialog";
import MicButton from "@/components/MicButton";

export default function PositionMappingStep() {
  const { map, update, setStep, setAiHelped } = useLab();
  const solo = isSolo(map);

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h2 className="font-serif text-2xl sm:text-3xl tracking-tight text-[#2D2A26]">What's really going on underneath?</h2>
        <PositionsVsNeedsDialog />
      </div>

      <div className="rounded-2xl bg-[#F5F2EA] border border-[#E8E3D9] p-6 mb-6">
        <p className="text-sm uppercase tracking-[0.2em] text-[#6E6860]">The disagreement</p>
        <p className="font-serif text-xl text-[#2D2A26] mt-1" data-testid="mapping-issue">{map.issue}</p>
      </div>

      <p className="text-[#2D2A26] leading-relaxed mb-4">
        Beneath each position is a deeper feeling or need — what each of you truly cares about.
        {solo
          ? " Speak for yourself first, then take a caring guess at your partner's side."
          : " Take turns. Speak for yourself, gently, and try not to explain the other person's side for them."}
      </p>
      {solo ? (
        <div className="rounded-xl bg-[#F5F2EA] border border-[#E8E3D9] p-4 mb-6 text-sm text-[#6E6860] leading-relaxed" data-testid="solo-hint">
          You're doing this on your own — that's completely okay. Fill in your own feelings, then take your
          best, caring guess at what your partner might be feeling (or tap <span className="font-medium text-[#2D2A26]">Suggest feelings</span> and let AI guess for you). You can always revisit it together later.
        </div>
      ) : (
        <div className="rounded-xl bg-[#F5F2EA] border border-[#E8E3D9] p-4 mb-6 text-sm text-[#6E6860] leading-relaxed" data-testid="together-hint">
          You're doing this together — wonderful. Each of you, fill in your <span className="font-medium text-[#2D2A26]">own</span> column in your own words. It's okay to pass the keyboard back and forth.
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-5">
        <FeelingsColumn
          accent="#4A5D4E"
          name={nameA(map)}
          position={map.position_a}
          value={map.concerns_a}
          onChange={(v) => update({ concerns_a: v })}
          issue={map.issue}
          side="a"
          setAiHelped={setAiHelped}
          prompt={solo ? "What do you really feel or need here? What are you worried about, or hoping for?" : `${nameA(map)}, what do you really feel or need here? What are you worried about, or hoping for?`}
          guess={false}
        />
        <FeelingsColumn
          accent="#C4A265"
          name={nameB(map)}
          position={map.position_b}
          value={map.concerns_b}
          onChange={(v) => update({ concerns_b: v })}
          issue={map.issue}
          side="b"
          setAiHelped={setAiHelped}
          prompt={solo ? `What might ${nameB(map)} really be feeling or needing here?` : `${nameB(map)}, what do you really feel or need here? What are you worried about, or hoping for?`}
          guess={solo}
        />
      </div>

      <div className="mt-10 flex justify-between">
        <button onClick={() => setStep(0)} data-testid="mapping-back-button" className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-[#6E6860] hover:bg-[#F5F2EA] transition-colors">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <button
          onClick={() => setStep(2)}
          data-testid="mapping-continue-button"
          className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#4A5D4E] text-[#FDFBF7] font-medium hover:bg-[#3B4A3E] transition-all hover:-translate-y-0.5"
        >
          Continue <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </motion.div>
  );
}

function FeelingsColumn({ accent, name, position, value, onChange, issue, side, setAiHelped, prompt, guess }) {
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  const suggest = async () => {
    if (!position?.trim()) {
      toast.error(`Add ${name}'s view on the previous step first.`);
      return;
    }
    setLoading(true);
    setAiHelped(true);
    try {
      const { data } = await api.post("/ai/concerns", { issue, position });
      setSuggestions(data.suggestions || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setLoading(false);
  };

  const addSuggestion = (s) => {
    const prefix = value?.trim() ? value.trim() + "\n" : "";
    onChange(prefix + "• " + s);
    setSuggestions((prev) => prev.filter((x) => x !== s));
  };

  return (
    <div className="rounded-2xl bg-white border border-[#E8E3D9] p-6">
      <p className="text-sm uppercase tracking-[0.2em] mb-1" style={{ color: accent }}>{name}</p>
      <p className="text-sm text-[#6E6860] mb-4 line-clamp-3">{position || "—"}</p>
      <label className="block text-sm font-medium text-[#2D2A26] mb-2 pt-4 border-t border-[#E8E3D9]">
        {prompt}
      </label>
      {guess && (
        <p className="text-xs text-[#A39E94] mb-2">Best guess is fine — or tap “Suggest feelings” to let AI guess.</p>
      )}
      <div className="relative">
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={5}
          placeholder={guess ? "Your best guess at how they might feel…" : "I feel… I worry that… I really need…"}
          data-testid={`concerns-input-${side}`}
          className="w-full rounded-xl border border-[#E8E3D9] bg-white px-4 py-3 pr-12 text-[#2D2A26] placeholder:text-[#A39E94] focus:ring-2 focus:ring-[#4A5D4E] focus:outline-none transition resize-none"
        />
        <MicButton value={value} onChange={onChange} className="absolute bottom-2.5 right-2.5" testId={`concerns-mic-${side}`} />
      </div>
      <button
        onClick={suggest}
        disabled={loading}
        data-testid={`suggest-concerns-button-${side}`}
        className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm border border-[#4A5D4E]/30 text-[#4A5D4E] hover:bg-[#F5F2EA] transition-all disabled:opacity-60"
      >
        <Sparkles className={`h-4 w-4 ${loading ? "animate-pulse" : ""}`} />
        {loading ? "Thinking…" : guess ? "Suggest feelings (AI guess)" : "Suggest feelings"}
      </button>
      {suggestions.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {suggestions.map((s, i) => (
            <button
              key={i}
              onClick={() => addSuggestion(s)}
              data-testid={`concern-suggestion-${side}-${i}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm bg-[#F5F2EA] text-[#2D2A26] border border-[#E8E3D9] hover:bg-[#D9C5B2]/40 transition-all text-left"
            >
              <Plus className="h-3.5 w-3.5 text-[#4A5D4E]" /> {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
